/** Storage helpers */

import { info } from "./rollbar";

interface Options {
  useSessionStorage?: boolean;
}

/** Types that can be JSON-stringified */
// eslint-disable-next-line @typescript-eslint/ban-types
type Stringifiable = boolean | null | number | object | string;

/**
 * localStorage key name enum.
 *
 * You should only add a new key if you need to persist data independently of a user.
 * Otherwise, use a persisted substate in redux state.
 *
 * All StorageKeys in this repo should start with `campaignWeb`.
 * These keys are shared with duolingo-web, so we want to avoid any conflicts
 */
export enum StorageKey {
  RollbarThrottling = "campaignWeb.rollbarThrottling",
}

/**
 * We simply use a Map object to emulate window.localStorage if the latter
 * isn't available. Nothing will actually be persisted between page reloads,
 * but at least the app will still be usable.
 *
 * localForage is a more aggressive option that we could consider if we really
 * wanted persistence on all browsers, but Art thinks it's overkill for now.
 * https://github.com/localForage/localForage
 *
 * TODO: even if localStorage is available it can still throw exceptions,
 *  so we should probably fix code that relies on storage always working
 *  and then remove this fallback.
 */
const memoryStorage: Storage = (() => {
  const storage = new Map();
  return {
    clear: () => storage.clear(),
    getItem: key => storage.get(key) ?? null,
    key: index => Array.from(storage.keys())[index],
    get length() {
      return storage.size;
    },
    removeItem: key => storage.delete(key),
    setItem: (key, value) => storage.set(key, value),
  };
})();

let didLogFallback = false;

export const getItem = (key: string, options?: Options) => {
  try {
    const json = getStorage(options).getItem(key);
    return json ? (JSON.parse(json) as Stringifiable) : undefined;
  } catch {
    return undefined;
  }
};

const getStorage = (options?: Options) => {
  if (options?.useSessionStorage) {
    return sessionStorage;
  } else if (localStorage) {
    return localStorage;
  }
  // Use a variable instead of Rollbar's `once` option to avoid cluttering the console.
  if (!didLogFallback) {
    info("localStorage unavailable. Falling back to memoryStorage.");
    didLogFallback = true;
  }
  return memoryStorage;
};

export const hasItem = (key: string, options?: Options) => {
  try {
    return key in getStorage(options);
  } catch {
    return false;
  }
};

export const removeItem = (key: string, options?: Options) => {
  try {
    getStorage(options).removeItem(key);
  } catch {
    // localStorage is read-only in Safari private mode :(
  }
};

export const setItem = (
  key: string,
  value: Stringifiable,
  options?: Options,
) => {
  try {
    getStorage(options).setItem(key, JSON.stringify(value));
  } catch {
    // localStorage is read-only in Safari private mode :(
  }
};
